/**
 * ValidDemandsTab — Vue "Demandes valides" pour le Responsable Op. & Log.
 *
 * Agrège 3 sources :
 *  1. Besoins actifs (needs.status ≠ annule)
 *  2. Achats validés (expenses.status ∈ approved|completed)
 *  3. Bons à crédit autorisés (employee_orders.status = authorized)
 *
 * Actions : consultation (lecture seule) + marquer "Traité" / "Non traité".
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  ClipboardList, ShoppingCart, Wallet, ChevronDown, ChevronRight,
  Check, RotateCcw, Loader2, RefreshCw, Package, MapPin, Zap, Calendar, User, Filter,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (v) => (Math.round(v || 0)).toLocaleString('fr-FR');

const LOCATION_LABELS = {
  salle: "Salle", salle_jeux: "Salle de jeux", jardin: "Jardin",
  cuisine: "Cuisine", toilettes: "Toilettes", autres: "Autres",
};

export default function ValidDemandsTab({ currentUser }) {
  const [data, setData] = useState({ needs: [], purchases: [], credit_orders: [], counts: {} });
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [statusFilter, setStatusFilter] = useState("pending"); // 'pending' | 'processed' | 'all'
  const [categoryFilter, setCategoryFilter] = useState("all"); // 'all' | 'needs' | 'purchases' | 'credit_orders'
  const [open, setOpen] = useState({ needs: true, purchases: true, credit_orders: true });

  const load = async () => {
    setLoading(true);
    try {
      // Le Manager ne voit QUE ses propres demandes ; l'Admin voit tout.
      const isManager = currentUser?.role === "manager";
      const myName = currentUser?.name || currentUser?.username || "";
      const params = new URLSearchParams({ include_processed: String(statusFilter !== "pending") });
      if (isManager && myName) params.set("requested_by", myName);
      const r = await axios.get(`${API}/ops-requests/valid?${params.toString()}`);
      setData(r.data);
    } catch (e) {
      toast.error("Erreur : " + (e?.response?.data?.detail || e.message));
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [statusFilter]);

  const toggleProcess = async (kind, item) => {
    const wasProcessed = item.processed;
    const url = `${API}/ops-requests/${kind}/${item.id}/${wasProcessed ? "unprocess" : "process"}`;
    setBusyId(`${kind}:${item.id}`);
    try {
      await axios.post(url, wasProcessed ? {} : { processed_by: currentUser?.username || currentUser?.name || "manager" });
      toast.success(wasProcessed ? "Marqué non traité" : "Marqué comme traité");
      await load();
    } catch (e) {
      toast.error("Erreur : " + (e?.response?.data?.detail || e.message));
    } finally { setBusyId(null); }
  };

  const counts = data.counts || {};

  // Apply status + category filters to sections
  const filterItems = (items) => {
    if (statusFilter === "pending") return items.filter((x) => !x.processed);
    if (statusFilter === "processed") return items.filter((x) => x.processed);
    return items;
  };

  const allSections = [
    {
      key: "needs", label: "Besoins", icon: ClipboardList, accent: "#6366f1",
      items: filterItems(data.needs || []), render: NeedCard,
    },
    {
      key: "purchases", label: "Achats validés", icon: ShoppingCart, accent: "#a855f7",
      items: filterItems(data.purchases || []), render: PurchaseCard,
    },
    {
      key: "credit_orders", label: "Bons à crédit autorisés", icon: Wallet, accent: "#f59e0b",
      items: filterItems(data.credit_orders || []), render: CreditCard,
    },
  ];

  const sections = categoryFilter === "all" ? allSections : allSections.filter((s) => s.key === categoryFilter);

  // KPI counts (from server counts + local for tabs)
  const pendingCount = counts.pending || 0;
  const totalCount = counts.total || 0;
  const processedCount = Math.max(0, totalCount - pendingCount);

  return (
    <div className="space-y-3" data-testid="valid-demands-tab">
      {/* ===== Top-bar compacte ===== */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <div className="w-9 h-9 rounded-lg bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center flex-shrink-0">
            <ClipboardList className="w-5 h-5 text-emerald-400" strokeWidth={2.4} />
          </div>
          <div className="min-w-0">
            <p className="text-white font-bold text-[15px] leading-tight truncate">Demandes valides</p>
            <p className="text-slate-500 text-[11.5px] leading-tight">
              {currentUser?.role === "manager"
                ? <>Mes demandes · <span className="text-amber-300 font-semibold">{pendingCount}</span> en attente / {totalCount}</>
                : <>À suivre Op. · <span className="text-amber-300 font-semibold">{pendingCount}</span> en attente / {totalCount}</>}
            </p>
          </div>
        </div>
        <button
          onClick={load}
          disabled={loading}
          className="flex items-center gap-1.5 h-9 px-3 rounded-lg bg-slate-800 border border-slate-700 text-slate-300 hover:bg-slate-700 text-[12px] font-bold disabled:opacity-50 flex-shrink-0"
          data-testid="valid-demands-refresh"
        >
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" strokeWidth={2.4} />}
          Rafraîchir
        </button>
      </div>

      {/* ===== Tabs statut (pending / processed / all) ===== */}
      <div className="flex gap-1.5 overflow-x-auto pb-1 -mx-1 px-1">
        {[
          { value: "pending", label: "En attente", count: pendingCount, accent: "text-amber-300", ring: "ring-amber-400/60", bg: "bg-amber-500/15" },
          { value: "processed", label: "Traités", count: processedCount, accent: "text-emerald-300", ring: "ring-emerald-400/60", bg: "bg-emerald-500/15" },
          { value: "all", label: "Tous", count: totalCount, accent: "text-slate-200", ring: "ring-slate-400/60", bg: "bg-slate-500/15" },
        ].map((tab) => {
          const active = statusFilter === tab.value;
          return (
            <button
              key={tab.value}
              onClick={() => setStatusFilter(tab.value)}
              data-testid={`valid-demands-status-${tab.value}`}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-all ${
                active
                  ? `${tab.bg} ${tab.accent} ring-1 ${tab.ring}`
                  : "bg-slate-800/50 text-slate-400 hover:text-white hover:bg-slate-800"
              }`}
            >
              {tab.label}
              <span className={`rounded-full px-1.5 min-w-[20px] text-center text-[10px] leading-4 ${active ? "bg-white/10" : "bg-slate-700/50"}`}>
                {tab.count}
              </span>
            </button>
          );
        })}
      </div>

      {/* ===== Pills catégorie ===== */}
      <div className="flex items-center gap-1.5 overflow-x-auto pb-1 -mx-1 px-1">
        <Filter className="w-3.5 h-3.5 text-slate-500 flex-shrink-0" />
        {[
          { value: "all", label: "Toutes les demandes", icon: null },
          { value: "needs", label: "Besoins", icon: ClipboardList, color: "text-indigo-300" },
          { value: "purchases", label: "Achats", icon: ShoppingCart, color: "text-purple-300" },
          { value: "credit_orders", label: "Bons à crédit", icon: Wallet, color: "text-amber-300" },
        ].map((cat) => {
          const active = categoryFilter === cat.value;
          const Icon = cat.icon;
          return (
            <button
              key={cat.value}
              onClick={() => setCategoryFilter(cat.value)}
              data-testid={`valid-demands-cat-${cat.value}`}
              className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium whitespace-nowrap transition-all ${
                active
                  ? "bg-white/10 text-white ring-1 ring-white/20"
                  : "bg-slate-800/40 text-slate-400 hover:text-white"
              }`}
            >
              {Icon && <Icon className={`w-3 h-3 ${cat.color || ""}`} />}
              {cat.label}
            </button>
          );
        })}
      </div>

      {/* Sections */}
      {sections.length === 0 && (
        <div className="rounded-lg border border-slate-700 bg-slate-800/30 py-10 text-center">
          <ClipboardList className="w-10 h-10 mx-auto mb-2 opacity-40 text-slate-300" />
          <p className="text-slate-300 text-sm">Aucune demande dans cette catégorie.</p>
        </div>
      )}
      {sections.map((s) => {
        const Icon = s.icon;
        const isOpen = open[s.key];
        const items = s.items;
        const pending = items.filter((x) => !x.processed).length;
        return (
          <div key={s.key} className="rounded-xl bg-slate-900/40 border border-slate-700/50 overflow-hidden">
            <button
              onClick={() => setOpen({ ...open, [s.key]: !isOpen })}
              className="w-full flex items-center gap-3 p-3 hover:bg-slate-800/40 transition-colors"
              data-testid={`valid-demands-section-${s.key}`}
            >
              {isOpen ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
              <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${s.accent}22`, border: `1px solid ${s.accent}55` }}>
                <Icon className="w-4 h-4" style={{ color: s.accent }} strokeWidth={2.4} />
              </div>
              <p className="text-white font-bold text-[13.5px] flex-1 text-left">{s.label}</p>
              <span className="text-slate-400 text-[11.5px] font-mono">
                {pending} en attente / {items.length} total
              </span>
            </button>
            {isOpen && (
              <div className="p-3 pt-0 space-y-2">
                {items.length === 0 ? (
                  <p className="text-slate-600 italic text-[12px] text-center py-6">Aucune donnée dans cette catégorie.</p>
                ) : (
                  items.map((it) => (
                    <s.render
                      key={it.id}
                      item={it}
                      onToggle={() => toggleProcess(s.key === "credit_orders" ? "credit_order" : s.key.slice(0, -1), it)}
                      busy={busyId === `${s.key === "credit_orders" ? "credit_order" : s.key.slice(0, -1)}:${it.id}`}
                    />
                  ))
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════
//  Cards
// ═════════════════════════════════════════════════════════════════════

function ProcessButton({ processed, onToggle, busy }) {
  return (
    <button
      onClick={onToggle}
      disabled={busy}
      className={`flex items-center gap-1.5 h-8 px-2.5 rounded-lg text-[11.5px] font-bold border transition-colors disabled:opacity-50 ${
        processed
          ? "bg-slate-800 border-slate-700 text-slate-400 hover:text-slate-200"
          : "bg-emerald-500 border-emerald-400 text-slate-900 hover:bg-emerald-400"
      }`}
      data-testid={`toggle-processed-${processed ? "undo" : "mark"}`}
    >
      {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : (processed ? <RotateCcw className="w-3 h-3" strokeWidth={2.6} /> : <Check className="w-3 h-3" strokeWidth={2.6} />)}
      {processed ? "Non traité" : "Traité"}
    </button>
  );
}

function ProcessedBadge({ processed_by, processed_at }) {
  if (!processed_at) return null;
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-emerald-500/10 border border-emerald-500/25 text-emerald-300 text-[10px] font-bold uppercase tracking-wide">
      <Check className="w-2.5 h-2.5" strokeWidth={3} />
      Traité{processed_by ? ` · ${processed_by}` : ""} · {(processed_at || "").slice(0, 10)}
    </span>
  );
}

function BaseCard({ children, processed }) {
  return (
    <div className={`p-3 rounded-lg border ${
      processed
        ? "bg-slate-950/40 border-slate-800/60 opacity-70"
        : "bg-slate-800/40 border-slate-700/50"
    }`}>
      {children}
    </div>
  );
}

function NeedCard({ item, onToggle, busy }) {
  const items = item.items || [];
  return (
    <BaseCard processed={item.processed}>
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            {item.urgency === "urgente" && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-rose-500/15 border border-rose-500/30 text-rose-300 text-[9.5px] font-black uppercase">
                <Zap className="w-2.5 h-2.5" strokeWidth={3} /> Urgent
              </span>
            )}
            <span className="inline-flex items-center gap-1 text-slate-400 text-[10.5px]">
              <MapPin className="w-3 h-3" /> {LOCATION_LABELS[item.location] || item.location || "—"}
            </span>
            <ProcessedBadge processed_by={item.processed_by} processed_at={item.processed_at} />
          </div>
          <p className="text-white text-[13px] font-semibold leading-tight">{item.description || "(Sans description)"}</p>
          {items.length > 0 && (
            <ul className="mt-1.5 space-y-0.5">
              {items.slice(0, 5).map((it, i) => (
                <li key={i} className="text-slate-400 text-[11.5px] flex items-center gap-1.5">
                  <Package className="w-2.5 h-2.5" />
                  <span className="truncate">{it.description || "(article)"}</span>
                  <span className="text-slate-500 font-mono">×{it.quantity || 1}</span>
                </li>
              ))}
              {items.length > 5 && <li className="text-slate-500 text-[10.5px]">+ {items.length - 5} autre(s)…</li>}
            </ul>
          )}
          <div className="flex items-center gap-3 mt-2 text-[10.5px] text-slate-500">
            <span className="flex items-center gap-1"><User className="w-2.5 h-2.5" /> {item.requested_by || "—"}</span>
            <span className="flex items-center gap-1"><Calendar className="w-2.5 h-2.5" /> {(item.created_at || "").slice(0, 10)}</span>
            {item.amount > 0 && <span className="text-amber-300 font-mono">{fmt(item.amount)} F</span>}
          </div>
        </div>
        <ProcessButton processed={item.processed} onToggle={onToggle} busy={busy} />
      </div>
    </BaseCard>
  );
}

function PurchaseCard({ item, onToggle, busy }) {
  const supplier = item.supplier || item.supplier_name || "—";
  const status = item.status;
  return (
    <BaseCard processed={item.processed}>
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9.5px] font-black uppercase ${
              status === "completed"
                ? "bg-emerald-500/15 border border-emerald-500/30 text-emerald-300"
                : "bg-purple-500/15 border border-purple-500/30 text-purple-300"
            }`}>
              {status === "completed" ? "Validé" : "Approuvé"}
            </span>
            <span className="text-slate-400 text-[10.5px]">{item.category || item.type || "Achat"}</span>
            <ProcessedBadge processed_by={item.processed_by} processed_at={item.processed_at} />
          </div>
          <p className="text-white text-[13px] font-semibold leading-tight truncate">
            {item.description || item.title || `Achat ${(item.id || "").slice(0, 8)}`}
          </p>
          <div className="flex items-center gap-3 mt-2 text-[10.5px] text-slate-500 flex-wrap">
            <span className="flex items-center gap-1"><ShoppingCart className="w-2.5 h-2.5" /> {supplier}</span>
            <span className="flex items-center gap-1"><Calendar className="w-2.5 h-2.5" /> {(item.created_at || "").slice(0, 10)}</span>
          </div>
        </div>
        <ProcessButton processed={item.processed} onToggle={onToggle} busy={busy} />
      </div>
    </BaseCard>
  );
}

function CreditCard({ item, onToggle, busy }) {
  return (
    <BaseCard processed={item.processed}>
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-500/15 border border-amber-500/30 text-amber-300 text-[9.5px] font-black uppercase">
              Autorisé
            </span>
            <ProcessedBadge processed_by={item.processed_by} processed_at={item.processed_at} />
          </div>
          <p className="text-white text-[13px] font-semibold leading-tight">
            {item.employee_name || "Employé"} — {item.description || item.notes || "Bon à crédit"}
          </p>
          <div className="flex items-center gap-3 mt-2 text-[10.5px] text-slate-500 flex-wrap">
            <span className="flex items-center gap-1"><Calendar className="w-2.5 h-2.5" /> {(item.created_at || "").slice(0, 10)}</span>
            <span className="text-slate-400">Mois {item.month_period || "—"}</span>
            <span className="text-amber-300 font-mono ml-auto">{fmt(item.total)} F</span>
          </div>
        </div>
        <ProcessButton processed={item.processed} onToggle={onToggle} busy={busy} />
      </div>
    </BaseCard>
  );
}
